import React, { useState, useMemo, useEffect } from 'react';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import {
  fetchJobOffers,
  closeOfferAsAdmin,
  reopenOfferAsAdmin,
  deleteOfferAsAdmin,
  resolveSkillNames,
  type AdminJobOffer,
} from '../lib/offerService';
import { logger } from '../lib/logger';

const statusLabels: Record<string, string> = {
  active: 'Active',
  closed: 'Fermée',
  filled: 'Pourvue',
  closed_by_admin: 'Fermée (admin)',
};

const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border border-blue-200',
  closed: 'bg-gray-100 text-gray-500 border border-gray-200',
  filled: 'bg-green-50 text-green-700 border border-green-200',
  closed_by_admin: 'bg-red-50 text-red-700 border border-red-200',
};

function displaySkillLabel(code: string, map: Record<string, string>): string {
  if (!code) return '';
  if (code.startsWith('custom:')) return code.slice('custom:'.length);
  if (map[code]) return map[code];
  return code.replace(/[_-]+/g, ' ');
}

const contractLabels: Record<string, string> = {
  cdi: 'CDI',
  cdd: 'CDD',
  interim: 'Intérim',
  apprentissage: 'Apprentissage',
  stage: 'Stage',
  alternance: 'Alternance',
  extra: 'Extra',
  saisonnier: 'Saisonnier',
};

const JobOffers: React.FC = () => {
  const [offers, setOffers] = useState<AdminJobOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [seedFilter, setSeedFilter] = useState<string>('all');
  const [skillNameMap, setSkillNameMap] = useState<Record<string, string>>({});
  const [actingId, setActingId] = useState<string | null>(null);
  const [detailOffer, setDetailOffer] = useState<AdminJobOffer | null>(null);

  const reload = () => {
    setLoading(true);
    fetchJobOffers()
      .then(async (rows) => {
        setOffers(rows);
        const allCodes = Array.from(new Set(rows.flatMap((r) => r.requiredSkills)));
        const map = await resolveSkillNames(allCodes);
        setSkillNameMap(map);
      })
      .catch((err) => logger.error('load_job_offers', 'JobOffers', { error: String(err) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const handleClose = async (o: AdminJobOffer) => {
    const reason = window.prompt(
      `Fermer l'annonce "${o.title}" ?\n\nMotif (sera tracé dans admin_logs) :`,
      'Contenu non conforme',
    );
    if (!reason) return;
    setActingId(o.id);
    try {
      await closeOfferAsAdmin(o.id, reason);
      reload();
    } catch (err: any) {
      window.alert('Erreur : ' + (err?.message || err));
    } finally {
      setActingId(null);
    }
  };

  const handleReopen = async (o: AdminJobOffer) => {
    if (!window.confirm(`Réactiver l'annonce "${o.title}" ?`)) return;
    setActingId(o.id);
    try {
      await reopenOfferAsAdmin(o.id);
      reload();
    } catch (err: any) {
      window.alert('Erreur : ' + (err?.message || err));
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (o: AdminJobOffer) => {
    const reason = window.prompt(
      `Supprimer DÉFINITIVEMENT l'annonce "${o.title}" et toutes ses candidatures ?\n\nMotif (tracé dans admin_logs) :`,
      '',
    );
    if (!reason) return;
    if (!window.confirm(`Confirmer la suppression de "${o.title}" ?`)) return;
    setActingId(o.id);
    try {
      await deleteOfferAsAdmin(o.id, reason);
      reload();
    } catch (err: any) {
      window.alert('Erreur : ' + (err?.message || err));
    } finally {
      setActingId(null);
    }
  };

  const filtered = useMemo(() => {
    return offers.filter((o) => {
      const matchSearch =
        search === '' ||
        o.title.toLowerCase().includes(search.toLowerCase()) ||
        o.employerName.toLowerCase().includes(search.toLowerCase()) ||
        o.city.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || o.status === statusFilter;
      const matchSeed = seedFilter === 'all' || (seedFilter === 'seed' ? !!o.seedTag : !o.seedTag);
      return matchSearch && matchStatus && matchSeed;
    });
  }, [offers, search, statusFilter, seedFilter]);

  const columns: Column<AdminJobOffer>[] = [
    {
      key: 'title',
      header: 'Poste',
      render: (o) => (
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-gray-900">{o.title}</p>
            {o.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
          </div>
          <p className="text-xs text-gray-400">{o.employerName}</p>
        </div>
      ),
    },
    {
      key: 'sector',
      header: 'Secteur',
      render: (o) => (
        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md font-medium">
          {o.sectorCode}
        </span>
      ),
    },
    {
      key: 'city',
      header: 'Ville',
      render: (o) => <span className="text-gray-600">{o.city || '—'}</span>,
    },
    {
      key: 'contract',
      header: 'Contrat',
      render: (o) => (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
          {contractLabels[o.contractType] || o.contractType}
        </span>
      ),
    },
    {
      key: 'salary',
      header: 'Salaire',
      render: (o) => (
        <span className="text-gray-600 font-medium text-xs">
          {o.salaryMin && o.salaryMax
            ? `${o.salaryMin} – ${o.salaryMax} €`
            : o.salaryMin
            ? `${o.salaryMin} €+`
            : '—'}
        </span>
      ),
    },
    {
      key: 'candidates',
      header: 'Candidats',
      render: (o) => (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-md">
          👤 {o.applicationsCount}
        </span>
      ),
    },
    {
      key: 'skills',
      header: 'Compétences',
      render: (o) => {
        const skills = o.requiredSkills.slice(0, 3);
        const extra = o.requiredSkills.length - skills.length;
        if (skills.length === 0) return <span className="text-gray-400 text-xs">—</span>;
        return (
          <div className="flex flex-wrap gap-1 max-w-[260px]">
            {skills.map((c) => (
              <span key={c} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[10px] rounded">
                {displaySkillLabel(c, skillNameMap)}
              </span>
            ))}
            {extra > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] text-gray-500">+{extra}</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Statut',
      render: (o) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[o.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[o.status] || o.status}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Créée le',
      render: (o) => <span className="text-gray-500 text-xs">{o.createdAt}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (o) => {
        const acting = actingId === o.id;
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setDetailOffer(o)}
              className="px-2 py-1 text-xs rounded-md border border-gray-200 hover:bg-gray-50"
              title="Voir le détail"
            >
              👁
            </button>
            {o.status === 'active' ? (
              <button
                type="button"
                disabled={acting}
                onClick={() => handleClose(o)}
                className="px-2 py-1 text-xs rounded-md border border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                title="Fermer (modération)"
              >
                ⊘
              </button>
            ) : o.status === 'closed_by_admin' ? (
              <button
                type="button"
                disabled={acting}
                onClick={() => handleReopen(o)}
                className="px-2 py-1 text-xs rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                title="Réactiver"
              >
                ↺
              </button>
            ) : null}
            <button
              type="button"
              disabled={acting}
              onClick={() => handleDelete(o)}
              className="px-2 py-1 text-xs rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
              title="Supprimer"
            >
              🗑
            </button>
          </div>
        );
      },
    },
  ];

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous les statuts</option>
        <option value="active">Active</option>
        <option value="closed">Fermée</option>
        <option value="filled">Pourvue</option>
        <option value="closed_by_admin">Fermée (admin)</option>
      </select>
      <select
        value={seedFilter}
        onChange={(e) => setSeedFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous (réels + seeds)</option>
        <option value="real">Réels uniquement</option>
        <option value="seed">Seeds uniquement</option>
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">📋</span>
          <div>
            <p className="text-xl font-bold text-gray-900">{offers.length}</p>
            <p className="text-xs text-gray-500">Total offres</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">✅</span>
          <div>
            <p className="text-xl font-bold text-blue-600">{offers.filter(o => o.status === 'active').length}</p>
            <p className="text-xs text-gray-500">Actives</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-purple-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🎯</span>
          <div>
            <p className="text-xl font-bold text-purple-600">{offers.reduce((sum, o) => sum + o.applicationsCount, 0)}</p>
            <p className="text-xs text-gray-500">Candidatures totales</p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(o) => o.id}
        searchable
        searchPlaceholder="Rechercher par poste, entreprise, ville..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />

      {/* Drawer / modal de détail */}
      {detailOffer && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDetailOffer(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{detailOffer.title}</h2>
                <p className="text-sm text-gray-500">{detailOffer.employerName} · {detailOffer.city || '—'}</p>
              </div>
              <button
                type="button"
                onClick={() => setDetailOffer(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-gray-500">Statut :</span> <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${statusClasses[detailOffer.status] || ''}`}>{statusLabels[detailOffer.status] || detailOffer.status}</span></div>
              <div><span className="text-gray-500">Contrat :</span> {contractLabels[detailOffer.contractType] || detailOffer.contractType}</div>
              <div><span className="text-gray-500">Salaire :</span> {detailOffer.salaryMin && detailOffer.salaryMax ? `${detailOffer.salaryMin} – ${detailOffer.salaryMax} €` : detailOffer.salaryMin ? `${detailOffer.salaryMin} €+` : '—'}</div>
              <div><span className="text-gray-500">Candidatures :</span> {detailOffer.applicationsCount}</div>
              <div><span className="text-gray-500">Secteur :</span> {detailOffer.sectorCode || '—'}</div>
              <div><span className="text-gray-500">Créée le :</span> {detailOffer.createdAt}</div>
            </div>

            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Compétences requises ({detailOffer.requiredSkills.length})</p>
              {detailOffer.requiredSkills.length === 0 ? (
                <p className="text-sm text-gray-400">Aucune</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {detailOffer.requiredSkills.map((code) => {
                    const isCustom = code.startsWith('custom:');
                    const isUnresolved = !isCustom && !skillNameMap[code];
                    return (
                      <span
                        key={code}
                        className={`px-2 py-1 text-xs rounded-md ${
                          isUnresolved
                            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                            : isCustom
                              ? 'bg-purple-50 text-purple-700 border border-purple-200'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                        title={isUnresolved ? `Slug non résolu en DB : ${code}` : code}
                      >
                        {displaySkillLabel(code, skillNameMap)}
                        {isCustom && <span className="ml-1 opacity-60">(libre)</span>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
              {detailOffer.status === 'active' && (
                <button
                  type="button"
                  onClick={() => { handleClose(detailOffer); setDetailOffer(null); }}
                  className="px-3 py-1.5 text-sm rounded-md border border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  Fermer
                </button>
              )}
              {detailOffer.status === 'closed_by_admin' && (
                <button
                  type="button"
                  onClick={() => { handleReopen(detailOffer); setDetailOffer(null); }}
                  className="px-3 py-1.5 text-sm rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Réactiver
                </button>
              )}
              <button
                type="button"
                onClick={() => { handleDelete(detailOffer); setDetailOffer(null); }}
                className="px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobOffers;
